import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@tellescope/design-system";
import { snakeCaseToTitleCase } from "@tellescope/utils";
import { EligibilityErrorCard } from "../EligibilityErrorCard";
import type { BridgeEligibilityPhase } from "./useBridgeEligibilityCheck";

export interface BridgeEligibilityFieldContentProps {
  phase: BridgeEligibilityPhase;
  status?: string;
}

export const BridgeEligibilityFieldContent = ({ phase, status }: BridgeEligibilityFieldContentProps) => {
  const { t } = useTranslation("forms");

  switch (phase) {
    case "idle":
      return null;
    case "not-configured":
      return <EligibilityErrorCard message={t("Bridge Service Type IDs not configured")} />;
    case "loading-soft":
      return (
        <div className="flex flex-col items-center gap-2 py-6">
          <Spinner size="sm" />
          <p className="font-medium text-sm">{t("Checking eligibility...")}</p>
          <p className="text-muted-foreground text-xs">{t("This may take a few moments")}</p>
        </div>
      );
    case "polling-hard":
      return (
        <div className="flex flex-col items-center gap-2 py-6">
          <Spinner size="sm" />
          <p className="font-medium text-sm">{t("Verifying eligibility with insurance...")}</p>
          <p className="text-muted-foreground text-xs">{t("This usually takes 15-30 seconds")}</p>
        </div>
      );
    case "error":
      return <EligibilityErrorCard message={t("Please continue with the form.")} />;
    case "result":
    case "cached": {
      if (!status) {
        return <EligibilityErrorCard message={t("Please continue with the form.")} />;
      }
      if (status === "ELIGIBLE") {
        return (
          <div className="border border-green-500 rounded-md p-4 flex gap-3 items-start">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="font-semibold text-sm text-green-700">{t("Your insurance is accepted!")}</p>
          </div>
        );
      }
      return (
        <div className="border border-amber-500 rounded-md p-4 flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="font-semibold text-sm">
            {t("Eligibility Status: {{status}}", { status: snakeCaseToTitleCase(status) })}
          </p>
        </div>
      );
    }
  }
};
